import type { Logger } from '@vp/ubik-logging'

import { upsertCart, upsertCartMultiWork } from './CartManagerClient'
import { getCart as getCcv2Cart } from './Ccv2CartClient'
import type { Ccv2Cart, Ccv2LineItemOrGroup } from '../types/Ccv2Cart'
import type { WorkId } from '../types/WorkId'

/**
 * Schema-agnostic view of a cart's contents needed by this fragment.
 *
 * Callers only need to know which works are present in the cart; keeping the
 * concrete response schema out of the interface lets us swap backends without
 * touching callers.
 */
export type CartContents = {
  /** workIds present in the cart. */
  workIds: string[]
}

/**
 * Backend-agnostic contract for the cart operations the fragment needs.
 *
 * Implemented by {@link createCcv2CartApi} (CCv2 Cart API read + CartManager
 * write). The add-to-cart write stays on CartManager; its own CCv2 migration
 * is tracked separately (POT-1337).
 */
export interface CartApi {
  getCart(shopperId: string, authorizationHeader: string, logger: Logger): Promise<CartContents>
  upsertWork(workId: WorkId, culture: string, authorizationHeader: string, logger: Logger): Promise<void>
  upsertWorks(workIds: WorkId[], culture: string, authorizationHeader: string, logger: Logger): Promise<void>
}

const ccv2ToCartContents = (cart: Ccv2Cart): CartContents => {
  const workIds: string[] = []
  const collect = (items?: Ccv2LineItemOrGroup[]): void => {
    if (!items) return
    for (const item of items) {
      const workId = item.custom?.analytics?.workId
      if (workId) {
        workIds.push(workId)
      }
      // GROUP items carry nested line items.
      collect(item.items)
    }
  }
  collect(cart.items)
  return { workIds }
}

export type Ccv2CartApiConfig = {
  /** CCv2 key account id. */
  accountId: string
  /** CCv2 cart type space id. */
  cartTypeId: string
  /** Legacy CartManager tenant used for the (still-legacy) write path. */
  cartManagerTenant: string
}

/**
 * Cart API implementation: reads from the CCv2 Cart API; the add-to-cart write
 * still goes through CartManager (migrated separately under POT-1337).
 */
export const createCcv2CartApi = ({ accountId, cartTypeId, cartManagerTenant }: Ccv2CartApiConfig): CartApi => ({
  getCart: (shopperId, authorizationHeader, logger) => getCcv2Cart(accountId, shopperId, cartTypeId, authorizationHeader, logger).then(ccv2ToCartContents),
  upsertWork: (workId, culture, authorizationHeader, logger) => upsertCart(cartManagerTenant, workId, culture, authorizationHeader, logger),
  upsertWorks: (workIds, culture, authorizationHeader, logger) => upsertCartMultiWork(cartManagerTenant, workIds, culture, authorizationHeader, logger),
})
